// Kit compartido del submenú Restaurant (POS): tipos de los RPCs pos.rest_* y
// helpers de estado/UI. Ver scripts 317/318.
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { locationsList, posRpc } from './shared';
import type { LocationOpt } from './shared';
import { cacheGet, cacheSet, conexion } from './offline';

export interface RestConfigT {
  id: string; location_id: string; cobra_servicio: boolean; servicio_pct: number;
  servicio_producto_id: string | null; kds_verde_min: number; kds_amarillo_min: number;
  qr_autoservicio?: boolean;
}
export interface AreaT { id: string; nombre: string; orden: number; imagen_fondo?: string | null }
export interface CuentaResumenT {
  id: string; codigo: string; estado: string; mesero: string | null;
  comensales: number; minutos: number; total: number; tipo?: string;
  entrega_estado?: string | null; entrega_nombre?: string | null;
  entrega_direccion?: string | null; repartidor?: string | null;
}
export interface ReservaMesaT { id: string; hora: string; cliente: string; personas: number }
export interface MesaT {
  id: string; area_id: string; numero: number; capacidad: number;
  forma: 'circulo' | 'cuadrado' | 'rectangulo' | 'barra';
  pos_x: number; pos_y: number; ancho: number; alto: number;
  estado_manual: 'reservada' | 'sucia' | null;
  cuenta: CuentaResumenT | null;
  reserva?: ReservaMesaT | null;
}
export interface EstacionT {
  id: string; nombre: string; orden: number; imprime_comanda: boolean; activo?: boolean;
  modo_impresion?: 'terminal' | 'red'; impresora_host?: string | null; impresora_puerto?: number;
  categorias?: { id: string; nombre: string }[];
}
export interface ReservaT {
  id: string; mesa_id: string; mesa_numero: number; area: string | null;
  fecha_hora: string; cliente_nombre: string; telefono: string | null;
  personas: number; notas: string | null; estado: string;
}
export interface RepartidorT { id: string; nombre: string; telefono: string | null; activo: boolean }
export interface RestEstadoT {
  config: RestConfigT | null; areas: AreaT[]; mesas: MesaT[];
  llevar: CuentaResumenT[]; estaciones: EstacionT[];
}
export interface ModOpcionT { id: string; nombre: string; precio_extra: number; orden?: number; activo?: boolean }
export interface ModGrupoT {
  id?: string; grupo_id?: string; nombre: string; min_sel: number; max_sel: number;
  activo?: boolean; opciones: ModOpcionT[];
}
export interface LineaCuentaT {
  id: string; ronda: number | null; producto_id: string; codigo: string | null;
  descripcion: string; cantidad: number; precio_unitario: number; iva_tarifa: number;
  modificadores: { grupo: string; nombre: string; precio_extra: number }[] | null;
  notas: string | null; estado: string; estacion_id: string | null;
  venta_id: string | null; subtotal: number; curso?: number; asiento?: number | null;
  origen?: 'salon' | 'qr' | 'agregador';
}
export interface RecetaItemT {
  id?: string; ingrediente_id: string; codigo?: string; nombre?: string; unidad?: string | null;
  cantidad: number; costo_unitario?: number; costo?: number;
}
export interface FoodCostT {
  id: string; codigo: string; nombre: string; precio_base: number;
  costo_receta: number | null; foodcost_pct: number | null; ingredientes: number;
}
export interface EsperaT {
  id: string; nombre: string; personas: number; telefono: string | null;
  notas: string | null; estado: string; mesa_numero: number | null; minutos: number;
}
export interface PedidoExtT {
  id: string; proveedor: string; referencia_ext: string; estado: string;
  payload: { cliente?: string; telefono?: string; direccion?: string;
    items?: { producto_id: string; nombre?: string; cantidad: number; precio?: number }[] };
  cuenta_id: string | null; cuenta_codigo: string | null; created_at: string; minutos: number;
}
export interface AgregadorCfgT {
  id: string; proveedor: string; modo: string; api_key_configurada: boolean;
  comision_pct: number; activo: boolean;
}
export interface TotalesT { subtotal: number; servicio: number; iva: number; total: number }
export interface CuentaT {
  id: string; codigo: string; tipo: string; estado: string;
  mesa_id: string | null; mesa_numero: number | null; area: string | null;
  mesero_id: string | null; mesero: string | null; comensales: number;
  servicio_pct: number; abierta_at: string; minutos: number;
  observaciones: string | null; lineas: LineaCuentaT[]; totales: TotalesT;
  partes: number | null; partes_cobradas: number;
  entrega_nombre?: string | null; entrega_telefono?: string | null;
  entrega_direccion?: string | null; entrega_estado?: string | null;
  repartidor_id?: string | null; repartidor?: string | null;
}
export interface MeseroSesionT { id: string; nombre: string; es_supervisor: boolean; pin: string }
export interface KdsTicketT {
  cuenta_id: string; codigo: string; mesa_numero: number | null; tipo: string;
  mesero: string | null; ronda: number; estacion_id: string | null; estacion: string;
  estado: 'comandada' | 'preparando'; enviada_at: string; edad_seg: number;
  lineas: { id: string; cantidad: number; descripcion: string;
    modificadores: { nombre: string }[] | null; notas: string | null; estado: string }[];
}
export interface KdsListoT {
  cuenta_id: string; codigo: string; mesa_numero: number | null; ronda: number;
  estacion_id: string | null; estacion: string; lista_at: string; n: number;
}
export interface KdsDataT {
  umbrales: { verde_min: number; amarillo_min: number } | null;
  tickets: KdsTicketT[]; listos: KdsListoT[];
}
export interface ExpoTicketT {
  cuenta_id: string; codigo: string; mesa_numero: number | null; tipo: string;
  mesero: string | null; ronda: number; completo: boolean; enviada_at: string; edad_seg: number;
  estaciones: { estacion: string; estado: string; n: number }[];
  lineas: { id: string; cantidad: number; descripcion: string;
    modificadores: { nombre: string }[] | null; notas: string | null; estado: string; curso: number }[];
}
export interface ExpoDataT {
  umbrales: { verde_min: number; amarillo_min: number } | null;
  tickets: ExpoTicketT[];
  retenidos: { cuenta_id: string; codigo: string; mesa_numero: number | null; curso: number; n: number }[];
}
export const CURSOS = [
  { v: 1, l: '1º Entrada / Bebida' },
  { v: 2, l: '2º Plato fuerte' },
  { v: 3, l: '3º Postre / Café' },
];

export type EstadoMesa = 'libre' | 'ocupada' | 'por_cobrar' | 'reservada' | 'sucia';
export const estadoMesa = (m: MesaT): EstadoMesa =>
  m.cuenta ? (m.cuenta.estado === 'por_cobrar' ? 'por_cobrar' : 'ocupada')
    : m.reserva ? 'reservada'
    : (m.estado_manual ?? 'libre');

export const MESA_CLS: Record<EstadoMesa, string> = {
  libre: 'bg-emerald-100 border-emerald-400 text-emerald-900 dark:bg-emerald-900/30 dark:border-emerald-600 dark:text-emerald-100',
  ocupada: 'bg-sky-100 border-sky-500 text-sky-900 dark:bg-sky-900/40 dark:border-sky-500 dark:text-sky-100',
  por_cobrar: 'bg-orange-100 border-orange-500 text-orange-900 dark:bg-orange-900/40 dark:border-orange-500 dark:text-orange-100',
  reservada: 'bg-purple-100 border-purple-500 text-purple-900 dark:bg-purple-900/40 dark:border-purple-500 dark:text-purple-100',
  sucia: 'bg-red-50 border-red-400 text-red-900 dark:bg-red-900/30 dark:border-red-600 dark:text-red-100',
};
export const MESA_LABEL: Record<EstadoMesa, string> = {
  libre: 'Libre', ocupada: 'Ocupada', por_cobrar: 'Por cobrar', reservada: 'Reservada', sucia: 'Por limpiar',
};

export const GRID_COLS = 24;
export const GRID_ROWS = 14;

// ---------------------------------------------------------------------------
// OFFLINE (script 323): snapshots del plano/cuentas en IndexedDB (sobreviven
// Ctrl+R sin red) y registro local de cuentas cobradas offline por sincronizar.
// ---------------------------------------------------------------------------
export async function snapshotRestEstado(locationId: string, data: RestEstadoT): Promise<void> {
  await cacheSet(`rest_estado:${locationId}`, { data, ts: Date.now() });
}
export async function restEstadoOffline(locationId: string): Promise<RestEstadoT | null> {
  const c = await cacheGet<{ data: RestEstadoT }>(`rest_estado:${locationId}`);
  return c?.data ?? null;
}
export async function snapshotRestCuenta(cuenta: CuentaT): Promise<void> {
  await cacheSet(`rest_cuenta:${cuenta.id}`, { data: cuenta, ts: Date.now() });
}
export async function restCuentaOffline(id: string): Promise<CuentaT | null> {
  const c = await cacheGet<{ data: CuentaT }>(`rest_cuenta:${id}`);
  return c?.data ?? null;
}

// Cuentas cobradas OFFLINE (encoladas, por sincronizar): cuenta_id → client_uuid
export async function cobradasOffline(): Promise<Record<string, string>> {
  return (await cacheGet<Record<string, string>>('rest_cobradas')) ?? {};
}
export async function cobradaOfflineMarcar(cuentaId: string, clientUuid: string): Promise<void> {
  const m = await cobradasOffline();
  m[cuentaId] = clientUuid;
  await cacheSet('rest_cobradas', m);
}
export async function cobradasOfflineLimpiar(clientUuids: string[]): Promise<void> {
  const m = await cobradasOffline();
  for (const k of Object.keys(m)) if (clientUuids.includes(m[k])) delete m[k];
  await cacheSet('rest_cobradas', m);
}

// Hook de estado de conexión (indicador online/offline)
export function useConexion(): boolean {
  const [online, setOnline] = useState(conexion.online);
  useEffect(() => conexion.suscribir(setOnline), []);
  return online;
}

// Selector de local persistido (compartido por las pantallas Restaurant)
export function useLocalRestaurant() {
  const [locationId, setLocationId] = useState(() => localStorage.getItem('pos_rest_location') ?? '');
  const { data: locations = [] } = useQuery<LocationOpt[]>({ queryKey: ['pos-locations'], queryFn: locationsList });
  useEffect(() => {
    if (!locations.length) return;
    // Autocorrige un valor viejo de localStorage: si la bodega guardada no
    // pertenece al usuario/empresa actual (p.ej. sesión previa de otra empresa
    // en el mismo navegador), cae a la primera bodega válida. Sin esto,
    // rest_estado se llamaría con una bodega ajena y el plano saldría vacío.
    if (!locationId || !locations.some((l) => l.id === locationId)) {
      setLocationId(locations[0].id);
    }
  }, [locations, locationId]);
  useEffect(() => { if (locationId) localStorage.setItem('pos_rest_location', locationId); }, [locationId]);
  return { locationId, setLocationId, locations };
}

export const restEstadoQuery = (locationId: string) => ({
  queryKey: ['rest-estado', locationId],
  // write-through a IndexedDB: si la red cae (incluso tras Ctrl+R), el plano
  // se sirve del último snapshot (solo lectura + cobro offline)
  queryFn: async (): Promise<RestEstadoT> => {
    try {
      const d = await posRpc<RestEstadoT>('rest_estado', { _location: locationId });
      void snapshotRestEstado(locationId, d);
      return d;
    } catch (e) {
      const cached = await restEstadoOffline(locationId);
      if (cached) return cached;
      throw e;
    }
  },
  enabled: !!locationId,
  staleTime: 0,
  refetchOnMount: 'always' as const,
  refetchInterval: 6000,
});

// El teclado global de una pestaña keep-alive solo debe actuar si su contenedor
// está visible (getClientRects — offsetParent falla con position:fixed).
export const elementoVisible = (el: HTMLElement | null): boolean =>
  !!el && el.getClientRects().length > 0;
